import { types as eventTypes } from './enums/event';

/** Flags show up in a panel's yaxis gutter and are generated automatically (not saved) */
class Flag {
	/**
	 * @param {object} args Developer-defined custom args
	 * @property {object} style Flag style customization options
	 */
	constructor(args = {}) {
		/** @type {string} */
		this.id = args.id;
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {Modcharts} */
		this.core = args.panel.core;
		/** @type {Indicator} */
		this.indicator = args.indicator;
		/** @type {object} */
		this.el = {};
		/** @type {object} */
		this.style = this._getStyle(args);
		/** @type {string} */
		this.seriesId = args.seriesId;
	}

	/**
	 * Merge developer inputs with this default style
	 * @param {object} args Constructor args
	 * @returns {object} Default style for this type of flag
	 * @abstract
	 */
	_getStyle() {
		this.panel.core.warn("Required Flag method '_getStyle' not found");
	}

	/** Set the flag element's visibility to hidden */
	hide() {
		this.eventEmitter.emit(eventTypes[this.eventType.hide], { id: this.id });
		if (!this.isCustomRender) {
			Object.values(this.el).forEach(el => {
				el.style('visibility', 'hidden');
			});
		}
	}

	/** Show the flag element */
	show() {
		if (!this.panel.core.params.showFlags) return;
		if (this.id === 'crosshair.xAxis' && !this.panel.core.params.showXAxisFlag) return;
		this.eventEmitter.emit(eventTypes[this.eventType.show], { id: this.id });
		if (!this.isCustomRender) {
			Object.values(this.el).forEach(el => {
				el.style('visibility', null);
			});
		}
	}

	/**
	 * Abstract method to return label value. Must be overridden
	 * @abstract
	 * @private
	 */
	getValue() {
		return;
	}

	/** Remove the flag element from the DOM */
	remove() {
		if (this.el.flagBody) {
			const el = this.el.flagBody.node().parentNode;
			if (el) el.parentNode.removeChild(el);
		}
	}

	/**
	 * Create svg group ('<g>') for the flag and give it a classname
	 * @returns SVG flag group element
	 * @private
	 */
	getFlagGroupSVG() {
		const group = this.panel.rootOverlay.append('g');
		// add some classnames for potential developer customization (they are not needed by default)
		if (this.id) {
			group.node().classList.add(this.getClassName(`modcharts-flag-${this.id}`));
		}

		// Where appropriate, add a slightly more specific className.  One example of this is indicator flags, e.g. modcharts-flag-mae-maehigh / modcharts-flag-mae-maelow
		if (this.seriesId) {
			group.node().classList.add(this.getClassName(`modcharts-flag-${this.indicator.params.id}-${this.seriesId}`));
		}

		return group;
	}

	// convert id to a safe CSS className
	getClassName(id) {
		return id.toLowerCase().replace(/\./g, '-');
	}

	/**
	 * Create flag body inside flag svg group
	 * Group is passed in here because svg/d3 are a little awkward in creating elements with no parent
	 * @param {object} group A flag body SVG group - d3 selection
	 * @returns d3 svg polygon selection
	 * @private
	 */
	getFlagBody(group) {
		let body;
		if (this.style.shapeType === 'polygon') {
			body = group.append('polygon');
		} else if (this.style.shapeType === 'rectangle') {
			body = group.append('rect');
		} else {
			// Default to polygon if shapeType is not defined or is invalid
			body = group.append('polygon');
		}

		// background color
		body.style('fill', this.style.background.color);

		return body;
	}

	/**
	 * Create a label for the flag svg group.
	 * Group is passed in here because svg/d3 are a little awkward in creating
	 * elements with no parent
	 * @param {object} group A flag body SVG group - d3 selection
	 * @returns d3 svg text selection that will receive repeated updates to its
	 * value during crosshair updates
	 * @private
	 */
	getFlagLabel(group) {
		return group
			.append('text')
			.style('fill', this.style.color)
			.style('font-family', this.style.font.family)
			.style('font-size', `${this.style.font.size}px`)
			.attr('dy', '0.35em'); // dominant-baseline doesn't seem to work right in IE
	}

	/**
	 * Utility to get the appropriate padding value
	 * @param {string} side Which side's padding to get ('top', 'right', 'bottom', 'left')
	 * @returns {number} Calculated padding value
	 */
	_getPadding(side) {
		const { shapeType, polygon, rectangle, padding } = this.style;
		const yAxisSide = this.panel.core.params.yAxisSide;

		let shapePadding;

		if (shapeType === 'polygon') {
			shapePadding = polygon.yAxis[yAxisSide].label.padding[side];
		} else if (shapeType === 'rectangle') {
			shapePadding = rectangle.yAxis[yAxisSide].label.padding[side];
		}

		// Check if the padding value is valid (including 0), if not, use the default padding value
		return typeof shapePadding === 'number' ? shapePadding : padding;
	}

	/**
	 * Update flag body position, color
	 * @param {Panel} panel Panel to which this flag belongs
	 * @param {number} x New x-axis location for this flag
	 * @param {number} y New y-axis location for this flag
	 * @returns d3 svg polygon selection
	 */
	_updateFlagBody(panel, x, y) {
		if (!this.el.flagBody || window.isNaN(x) || window.isNaN(y)) return;

		const paddingTop = this._getPadding('top');
		const paddingBottom = this._getPadding('bottom');
		const paddingLeft = this._getPadding('left');
		const paddingRight = this._getPadding('right');
		const fontHeight = this.panel.params.style.yAxis.font.size;

		if (this.style.shapeType === 'polygon') {
			let points;
			let x2;
			const pointer = this.style.polygon.pointer;
			const yFlagBodyTop = y - paddingTop - fontHeight / 2;
			const yFlagBodyBottom = y + paddingBottom + fontHeight / 2;
			const yPointerTop = typeof pointer.height === 'number' ? y - pointer.height / 2 : yFlagBodyTop;
			const yPointerBottom = typeof pointer.height === 'number' ? y + pointer.height / 2 : yFlagBodyBottom;

			if (this.panel.core.params.yAxisSide === 'left') {
				x2 = x + panel.size.padding.left; // right edge of xaxis
				points = [
					[x2, y], // tip of the pointer
					[x2 - pointer.width, yPointerTop], // top right of pointer
					[x2 - pointer.width, yFlagBodyTop], // top left of flag body
					[x, yFlagBodyTop], // top right
					[x, yFlagBodyBottom], // bottom right
					[x2 - pointer.width, yFlagBodyBottom], // bottom left of flag body
					[x2 - pointer.width, yPointerBottom] // bottom right of pointer
				];
			} else {
				x2 = x + panel.size.padding.right; // right edge of xaxis
				points = [
					[x, y], // tip of the pointer
					[x + pointer.width, yPointerTop], // top right of pointer
					[x + pointer.width, yFlagBodyTop], // top left of flag body
					[x2, yFlagBodyTop], // top right
					[x2, yFlagBodyBottom], // bottom right
					[x + pointer.width, yFlagBodyBottom], // bottom left of flag body
					[x + pointer.width, yPointerBottom] // bottom right of pointer
				];
			}

			// Join points into a string for the 'points' SVG attribute
			points = points.map(point => point.join(' ')).join(',');
			this.el.flagBody.attr('points', points);
		} else if (this.style.shapeType === 'rectangle') {
			const width = Math.abs(panel.size.padding.right - panel.size.padding.left);
			const height = paddingTop + paddingBottom + fontHeight;
			this.el.flagBody
				.attr('x', x)
				.attr('y', y - paddingTop - fontHeight / 2)
				.attr('width', width)
				.attr('height', height)
				.attr('rx', this.style.rectangle.radius.x) // Rounded corners
				.attr('ry', this.style.rectangle.radius.y); // Rounded corners
		}

		// Update background color
		if (this.el.flagBody.style('fill') !== this.style.background.color) {
			this.el.flagBody.style('fill', this.style.background.color);
		}
	}

	/**
	 * Update flag label position, value, color
	 * @param {Panel} panel The panel to which this flag belongs
	 * @param {string} val The new flag label value
	 * @param {number} x The label's new x-axis location
	 * @param {number} y The label's new y-axis location
	 * @returns d3 svg polygon selection
	 */
	_updateFlagLabel(panel, val, x, y) {
		if (!this.el.flagLabel || window.isNaN(x) || window.isNaN(y)) return;

		// Update label text and position
		const formatString = this._getFlagFormatString(val);
		const formatValue = panel.yAxis.getFormatValue(val, formatString);
		const paddingLeft = this._getPadding('left');
		const paddingRight = this._getPadding('right');
		const leftYAxis = panel.core.params.yAxisSide === 'left';

		if (this.style.shapeType === 'polygon') {
			const pointerWidth = this.style.polygon.pointer.width;
			x = leftYAxis ? panel.size.padding.left - pointerWidth - paddingRight : x + pointerWidth + paddingLeft;
		} else { // rectangle
			x = leftYAxis ? panel.size.padding.left - paddingRight : x + paddingLeft;
		}
		this.el.flagLabel.attr('x', x);
		this.el.flagLabel.attr('y', y);
		this.el.flagLabel.attr('text-anchor', leftYAxis ? 'end' : 'start');

		// Update label value
		this.el.flagLabel.text(formatValue);

		// Update label font size
		this.el.flagLabel.style('font-size', `${this.panel.params.style.yAxis.font.size}px`);

		// Update label color
		if (this.style.background.color === this.style.color) {
			const fallbackColor = '#000000';
			this.panel.core.warn(`Flag label color is same as flag body. Using fallback color of ${fallbackColor}.`);
			this.el.flagLabel.style('fill', fallbackColor);
		} else if (this.el.flagLabel.style('fill') !== this.style.color) {
			this.el.flagLabel.style('fill', this.style.color);
		}
	}

	/** Update flag body color if something has changed */
	_resetFlagBackground() {
		// Update background color - this may have changed if the parent indicator's styles were modified
		const origBgColor = this.style.background.color;

		// If indicator has multiple flags, use default style, else use the indicator color
		if (this.indicator.params.componentSeries) {
			// Recalculate style
			this.style = this._getStyle({
				id: this.id,
				indicator: this.indicator,
				panel: this.panel,
				seriesId: this.seriesId
			});
		} else {
			const indicatorStyle = this.indicator
				? this.indicator.params.style
				: {};

			if (indicatorStyle.color) this.style.background.color = indicatorStyle.color;
		}

		// Update flag body color to match indicator color
		if (origBgColor !== this.style.background.color) {
			this.el.flagBody.style('fill', this.style.background.color);
		}
	}

	/**
	 * Update flag html and render the changes
	 * @param {number} val New value for the flag label
	 * @return {object}
	 * @property {number} val Flag label value
	 * @property {number} x X-axis location for flag
	 * @property {number} y Y-axis location for flag
	 * @property {number} top Flag panel's top location
	 * @property {number} bottom Flag panel's bottom location
	 */
	render(val = this.getValue()) {
		const { panel } = this;

		if (typeof val === 'number') {
			const height = this.panel.params.style.yAxis.font.size + this._getPadding('top') + this._getPadding('bottom');
			const halfHeight = height / 2;
			let x;
			if (panel.core.params.yAxisSide === 'left') {
				x = 0;
			} else {
				x = panel.size.width + panel.size.padding.left;
			}
			const y = panel.yAxis.primaryScale(val);
			const panelTop = panel.size.top;
			const yEdgeTest = panelTop + y;
			const panelBottom = panelTop + panel.size.height;

			// Hide if near top/bottom edges of panel. yEdgeTest takes into account top margin, since y is relative to zero value of scale.
			if (yEdgeTest + halfHeight > panelBottom || yEdgeTest - halfHeight < panelTop) {
				return this.hide();
			}
			// default render
			if (!this.isCustomRender) {
				// Create body and label once
				if (!this.el.flagBody) {
					const group = this.getFlagGroupSVG();
					this.el.flagBody = this.getFlagBody(group);
					this.el.flagLabel = this.getFlagLabel(group);
				}
				// Check if color params have changed, if so update SVG components
				if (this.indicator) this._resetFlagBackground();

				this._updateFlagBody(panel, x, y);
				this._updateFlagLabel(panel, val, x, y);
			} else {
				// Custom flag render
				this.eventEmitter.emit(eventTypes[this.eventType.render], {
					val,
					x,
					y,
					panelTop,
					panelBottom,
					id: this.id,
					panel,
					indicator: this.indicator,
					style: this.style
				});
			}

			this.show();
			return { val, x, y, top: panelTop, bottom: panelBottom };
		}
		return {};
	}

	/**
	 * Utility method to calculate the width of a specific flag's label
	 * @param {object} options Additional options such as padding
	 * @returns {number} The total width of the flag's label including padding
	 */
	_calculateFlagLabelWidth(options = {}) {
		const paddingLeft = this._getPadding('left');
		const paddingRight = this._getPadding('right');
		const pointerWidth = this.style.shapeType === 'polygon' ? this.style.polygon.pointer.width : 0;

		// Use the previously set text, font family, size, and weight from the flag instance
		const textElement = this.el.flagLabel;

		if (!textElement) return 0;

		const previousText = textElement.text();
		const fontFamily = this.style.font.family;
		const fontSize = this.style.font.size;

		// Apply the text and styles to the element (if not already applied)
		textElement.text(previousText)
			.style('font-family', fontFamily)
			.style('font-size', fontSize); // no weight supported yet

		// Get the bounding box of just the text element itself
		const bbox = textElement.node().getBBox();

		// Calculate the total width including any padding and pointer width (if applicable)
		const totalWidth = bbox.width + paddingLeft + paddingRight + pointerWidth;
		return totalWidth;
	}

	/**
	 * Return id of the appropriate label bucket (e.g. 'small', 'default', 'large')
	 * @returns {string} label bucket id
	 * @private
	 */
	_getFlagFormatType(val) {
		const { panel } = this;
		const { format: formatType } = panel.params.style.yAxis.label.flag;
		if (val >= formatType.large.threshold) {
			return 'large';
		} else if (val < formatType.small.threshold) {
			return 'small';
		}
		return 'default';
	}

	/**
	 * Return an appropriate d3-format string based on an input number (e.g. '0,.2f')
	 * @returns {string}
	 */
	_getFlagFormatString(val) {
		const { panel } = this;
		let formatString;
		const formatType = this._getFlagFormatType(val); // small, default, large
		const yLabelFormat = panel.params.style.yAxis.label.flag.format[formatType];
		if (panel.params.yAxis.format === 'percent' || panel.isNormalized) {
			// percent-change formatting
			formatString = yLabelFormat.formatPercent;
		} else {
			// default formatting
			formatString = yLabelFormat.format;
		}
		return formatString;
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 * @abstract
	 */
	get eventType() {
		throw new Error('Required Flag eventType getter not found');
	}

	/**
	 * @returns True if a custom render listener is enabled
	 * @type {boolean}
	 */
	get isCustomRender() {
		return this.eventEmitter.listeners(this.eventType.render).length > 0;
	}

	/**
	 * @returns Core event emitter
	 * @type {object}
	 */
	get eventEmitter() {
		return this.panel.core.eventEmitter;
	}
}

export default Flag;
